// src/utils/sessionTimeout.js
import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { clearAuth } from './fetchWithAuth';

/* Idle sign-out.

   This exists for shared devices -- the clock-in kiosk especially, where one
   phone or tablet gets handed between people and an abandoned session is a
   real problem.

   It used to fire after 20 minutes, with a blocking window.confirm at 18 and a
   window.alert on the way out. Three things were wrong with that:

     * 20 minutes of idle is nothing on a phone. Read a posting, take a call,
       come back, and you have been signed out. Most of this traffic is mobile,
       inside Meta's in-app browser, where backgrounding the tab is the normal
       way to use it.
     * It disagreed with the server, which keeps a token alive for 24 hours of
       inactivity and slides that window forward on every request. The client
       was ending sessions the API considered perfectly healthy.
     * window.alert and window.confirm are blocking modals. On mobile they hijack
       the screen, and an unanswered confirm just sits there. "Your session has
       expired" arriving in a system dialog is also, word for word, what users
       reported being told for reasons they could not explain.

   Now it sits just inside the server's window, so the server is the authority
   and this is only a backstop for a device someone walked away from. Nothing
   blocks; the message is a toast on the page they land on.

   Set VITE_IDLE_TIMEOUT_MINUTES to tighten it for a stricter deployment. */
const DEFAULT_IDLE_MINUTES = 12 * 60; // 12 hours, inside the server's 24h window

const IDLE_MINUTES = Number(import.meta.env?.VITE_IDLE_TIMEOUT_MINUTES) || DEFAULT_IDLE_MINUTES;
const TIMEOUT_DURATION = IDLE_MINUTES * 60 * 1000;

export function useSessionTimeout() {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);

  const logout = useCallback(() => {
    // Clear every key, including `bearer` and `token`, which the hand-written
    // list here used to miss -- leaving a stale credential behind that
    // isAuthenticated() would still answer true for.
    clearAuth();
    try {
      sessionStorage.clear();
    } catch {
      /* private mode */
    }
    navigate('/auth/login', { replace: true });
    toast('You were signed out after a long time away. Sign in to pick up where you left off.', {
      icon: '👋',
      duration: 6000,
    });
  }, [navigate]);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(logout, TIMEOUT_DURATION);
  }, [logout]);

  useEffect(() => {
    const hasToken =
      localStorage.getItem('access') ||
      localStorage.getItem('bearer') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('token');

    if (!hasToken) {
      return undefined; // nothing to time out
    }

    // Passive so scroll handling is never blocked on this.
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((event) => document.addEventListener(event, resetTimer, { passive: true }));

    // Coming back to a backgrounded tab is activity. Without this, a phone that
    // slept overnight would sign the user out the instant they reopened it,
    // which is the opposite of what an idle timer is for.
    const onVisible = () => {
      if (document.visibilityState === 'visible') resetTimer();
    };
    document.addEventListener('visibilitychange', onVisible);

    resetTimer();

    return () => {
      events.forEach((event) => document.removeEventListener(event, resetTimer));
      document.removeEventListener('visibilitychange', onVisible);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [resetTimer]);

  return null;
}

// Alternative: Component version
export function SessionTimeoutProvider({ children }) {
  useSessionTimeout();
  return children;
}
